import React, { useState, useRef, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { adminAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

interface ImportResult {
  totalRows: number;
  created: number;
  skipped: number;
  errors: { row: number; email: string; reason: string }[];
  batchId?: string;
  status?: string;
  message?: string;
  compensatedRows?: number;
  compensationFailedRows?: number;
}

interface ImportBatchStatusResponse {
  id: string;
  status: string;
  currentPhase: 'validating' | 'running' | 'compensating' | 'completed';
  totalRows: number;
  createdCount: number;
  skippedCount: number;
  reconciliationRequiredCount: number;
  failedCount: number;
  compensatedRows: number;
  compensationFailedRows: number;
  lastError: string | null;
  rows: Array<{
    row: number;
    email: string;
    status: string;
    reasonCode?: string | null;
    reason?: string | null;
    reconciliationAction?: string | null;
    reconciliationResult?: string | null;
  }>;
}

interface CsvPreview {
  headers: string[];
  totalRows: number;
  validRows: number;
  missingEmailRows: number;
  hasEmailColumn: boolean;
  sampleRows: Record<string, string>[];
}

type Step = 'upload' | 'preview' | 'results';

const ACTIVE_IMPORT_BATCH_STORAGE_KEY = 'admin.customerImport.activeBatchId';

interface CustomerImportModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Minimal CSV parser handling quoted fields
function parseCsvText(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const parseRow = (line: string): string[] => {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') { current += '"'; i++; }
        else { inQuotes = !inQuotes; }
      } else if (ch === ',' && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    fields.push(current.trim());
    return fields;
  };

  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };

  const headers = parseRow(lines[0]).map(h => h.replace(/^"|"$/g, '').toLowerCase().trim());
  const rows = lines.slice(1).map(line => {
    const values = parseRow(line);
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = (values[i] ?? '').replace(/^"|"$/g, '').trim(); });
    return obj;
  });

  return { headers, rows };
}

function buildPreview(file: File): Promise<CsvPreview> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const { headers, rows } = parseCsvText(text);
        const hasEmailColumn = headers.includes('email');
        const validRows = hasEmailColumn ? rows.filter(r => r['email']?.trim()).length : 0;
        const missingEmailRows = rows.length - validRows;
        resolve({
          headers,
          totalRows: rows.length,
          validRows,
          missingEmailRows,
          hasEmailColumn,
          sampleRows: rows.slice(0, 5),
        });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsText(file);
  });
}

export function CustomerImportModal({ isOpen, onClose }: CustomerImportModalProps) {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<CsvPreview | null>(null);
  const [results, setResults] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importProgress, setImportProgress] = useState(0);
  const [importStage, setImportStage] = useState('');
  const [activeBatchId, setActiveBatchId] = useState<string | null>(null);
  const [isBatchRunning, setIsBatchRunning] = useState(false);
  const pollErrorCountRef = useRef(0);
  const queryClient = useQueryClient();

  const importMutation = useMutation(
    (csvFile: File) => adminAPI.startCustomerImportBatch(csvFile),
    {
      onSuccess: (response) => {
        const data = response.data as { batchId: string; status: string; totalRows: number; message: string };
        setActiveBatchId(data.batchId);
        setIsBatchRunning(true);
        setImportProgress(5);
        setImportStage('Batch queued...');
        sessionStorage.setItem(ACTIVE_IMPORT_BATCH_STORAGE_KEY, data.batchId);
      },
      onError: (error: any) => {
        const preflightErrors = error?.response?.data?.preflightErrors;
        if (Array.isArray(preflightErrors)) {
          setResults({
            status: 'preflight_failed',
            message: 'Preflight validation failed',
            totalRows: preview?.totalRows || preflightErrors.length,
            created: 0,
            skipped: preflightErrors.length,
            errors: preflightErrors,
          });
          setStep('results');
          toast.error('Import preflight failed. No customers were created.');
          return;
        }

        const isTimeout = error?.code === 'ECONNABORTED'
          || String(error?.message || '').toLowerCase().includes('timeout');

        toast.error(
          isTimeout
            ? 'Import startup took too long. Please retry once; the server may still be warming up.'
            : (error.response?.data?.message || 'Failed to start import batch'),
        );
      },
    }
  );

  useEffect(() => {
    if (!isOpen || isBatchRunning || activeBatchId) return;

    const resumeBatchId = sessionStorage.getItem(ACTIVE_IMPORT_BATCH_STORAGE_KEY);
    if (!resumeBatchId) return;

    setActiveBatchId(resumeBatchId);
    setStep('preview');
    setIsBatchRunning(true);
    setImportStage('Resuming active import batch...');
    setImportProgress((current) => (current > 0 ? current : 5));
  }, [isOpen, isBatchRunning, activeBatchId]);

  useEffect(() => {
    if (!activeBatchId || !isBatchRunning) return;

    let cancelled = false;
    const poll = async () => {
      try {
        const response = await adminAPI.getCustomerImportBatchStatus(activeBatchId);
        if (cancelled) return;

        const data = response.data as ImportBatchStatusResponse;
        pollErrorCountRef.current = 0;

        const terminal = [
          'succeeded',
          'completed_with_skips',
          'completed_with_reconciliation_required',
          'failed_no_side_effects',
          'failed_compensated',
          'failed_manual_reconciliation_required',
        ].includes(data.status);

        const errors = data.rows
          .filter((row) => row.reason || row.reconciliationResult)
          .map((row) => ({
            row: row.row,
            email: row.email,
            reason: row.reason || row.reconciliationResult || 'Unknown error',
          }));

        setImportStage(data.currentPhase === 'validating'
          ? 'Running preflight validation...'
          : data.currentPhase === 'running'
            ? 'Creating local and Stripe customers...'
            : data.currentPhase === 'compensating'
              ? 'Compensating side effects...'
              : 'Finalizing...');

        const progress = data.totalRows > 0
          ? Math.min(
              95,
              Math.round(
                ((data.createdCount + data.skippedCount + data.reconciliationRequiredCount + data.failedCount)
                  / data.totalRows) * 95,
              ),
            )
          : 95;
        setImportProgress(terminal ? 100 : progress);

        if (terminal) {
          setIsBatchRunning(false);
          sessionStorage.removeItem(ACTIVE_IMPORT_BATCH_STORAGE_KEY);
          setResults({
            batchId: data.id,
            status: data.status,
            message: data.lastError || undefined,
            totalRows: data.totalRows,
            created: data.createdCount,
            skipped: data.skippedCount + data.reconciliationRequiredCount,
            compensatedRows: data.compensatedRows,
            compensationFailedRows: data.compensationFailedRows,
            errors,
          });
          setStep('results');
          queryClient.invalidateQueries(['admin-customers']);
          queryClient.refetchQueries(['admin-customers']);

          if (['succeeded', 'completed_with_skips', 'completed_with_reconciliation_required'].includes(data.status)) {
            toast.success(`Successfully imported ${data.createdCount} customer${data.createdCount === 1 ? '' : 's'}`);
          } else if (data.status === 'failed_no_side_effects') {
            toast.error('Import failed in preflight. No customers were created.');
          } else if (data.status === 'failed_compensated') {
            toast.error('Import failed but side effects were fully compensated.');
          } else {
            toast.error('Import failed and requires manual reconciliation.');
          }
        }
      } catch (error: any) {
        if (!cancelled) {
          pollErrorCountRef.current += 1;
          if (pollErrorCountRef.current === 1 || pollErrorCountRef.current % 5 === 0) {
            toast.error(error?.response?.data?.message || 'Temporary polling issue. Retrying import status...');
          }
        }
      }
    };

    void poll();
    const timer = window.setInterval(() => {
      void poll();
    }, 3000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [activeBatchId, isBatchRunning, queryClient, preview?.totalRows]);

  const handleDownloadTemplate = async () => {
    try {
      const response = await adminAPI.downloadCustomerImportTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customer-import-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    if (selected.size > 5 * 1024 * 1024) {
      toast.error('File is too large. Maximum size is 5MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFile(selected);
    setPreview(null);
    setResults(null);
    try {
      const p = await buildPreview(selected);
      setPreview(p);
      setStep('preview');
    } catch {
      toast.error('Could not parse CSV. Please check the file format.');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    if (!isBatchRunning) return;
    const estimated = Math.min(120000, Math.max(6000, (preview?.validRows ?? 50) * 200));
    const tickMs = 250;
    let elapsed = 0;
    setImportStage('Preparing import batch...');
    const timer = setInterval(() => {
      elapsed += tickMs;
      const capped = 90 * (1 - Math.exp(-3 * (elapsed / estimated)));
      setImportProgress((current) => Math.max(current, capped));
      if (elapsed < 800) {
        setImportStage('Preparing import batch...');
      } else if (elapsed < 2500) {
        setImportStage('Running preflight validation...');
      } else if (capped < 82) {
        setImportStage('Creating local and Stripe customers...');
      } else {
        setImportStage('Finalizing...');
      }
    }, tickMs);
    return () => clearInterval(timer);
  }, [isBatchRunning, preview?.validRows]);

  const handleConfirmImport = () => {
    if (file) importMutation.mutate(file);
  };

  const handleBack = () => {
    setStep('upload');
    setPreview(null);
    setActiveBatchId(null);
    setIsBatchRunning(false);
    importMutation.reset();
  };

  const handleClose = () => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setResults(null);
    setActiveBatchId(null);
    setIsBatchRunning(false);
    setImportStage('');
    setImportProgress(0);
    importMutation.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  if (!isOpen) return null;

  const modalWidth = step === 'preview' ? 'max-w-2xl' : 'max-w-lg';

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`bg-surface-container-lowest rounded-card shadow-float w-full ${modalWidth} max-h-[90vh] overflow-y-auto`}>

        {/* Header */}
        <div className="flex justify-between items-center p-6">
          <div>
            <h2 className="text-xl font-semibold text-on-surface">Import Customers from CSV</h2>
            <div className="flex items-center gap-2 mt-1">
              {(['upload', 'preview', 'results'] as Step[]).map((s, i) => (
                <React.Fragment key={s}>
                  <span className={`text-xs font-medium ${step === s ? 'text-blue-600' : 'text-on-surface-variant'}`}>
                    {i + 1}. {s.charAt(0).toUpperCase() + s.slice(1)}
                  </span>
                  {i < 2 && <span className="text-on-surface-variant text-xs">›</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
          <button onClick={handleClose} className="text-on-surface-variant hover:text-on-surface">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="h-px bg-surface-dim" />

        {/* ── Step: Upload ── */}
        {step === 'upload' && (
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-sm font-semibold text-on-surface-variant mb-2">Step 1: Download the CSV template</h3>
              <button
                onClick={handleDownloadTemplate}
                className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                <span>Download Template</span>
              </button>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-on-surface-variant mb-2">Step 2: Fill in customer data and upload</h3>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="block w-full text-sm text-on-surface-variant file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            <div className="bg-surface-container-low rounded-lg p-4 text-sm text-on-surface-variant space-y-1">
              <p><strong>Email</strong> is the only required column</p>
              <p>Duplicate emails (already in your Stripe account) will be skipped</p>
              <p>Maximum 500 rows per upload</p>
              <p>Valid customer types: Corporate, Affiliate, DMC/Event, Hotel, Association, School, Retail, Individual, Wedding</p>
            </div>
          </div>
        )}

        {/* ── Step: Preview / Importing ── */}
        {step === 'preview' && (importMutation.isLoading || isBatchRunning) && (
          <div className="p-6 space-y-6">
            <div className="text-center py-2">
              <p className="text-base font-semibold text-on-surface mb-1">{importStage}</p>
              <p className="text-sm text-on-surface-variant mb-5">
                {preview
                  ? `Processing ${preview.validRows} customer${preview.validRows !== 1 ? 's' : ''}...`
                  : 'Processing active import batch...'}
              </p>
              <div className="w-full bg-surface-container-low rounded-full h-2.5 mb-1.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${importProgress}%` }}
                />
              </div>
              <p className="text-xs text-on-surface-variant">{Math.round(importProgress)}%</p>
            </div>
            <div className="space-y-3">
              {([
                { label: 'Upload CSV', doneAt: 8 },
                { label: 'Validate rows', doneAt: 25 },
                { label: 'Create customers in Stripe', doneAt: 85 },
                { label: 'Finalize', doneAt: 100 },
              ] as { label: string; doneAt: number }[]).map(({ label, doneAt }, i, arr) => {
                const done = importProgress >= doneAt;
                const active = !done && importProgress >= (arr[i - 1]?.doneAt ?? 0);
                return (
                  <div key={label} className="flex items-center gap-2.5 text-sm">
                    {done ? (
                      <CheckCircleIcon className="h-4 w-4 text-green-600 shrink-0" />
                    ) : active ? (
                      <svg className="animate-spin h-4 w-4 text-blue-600 shrink-0" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <div className="h-4 w-4 rounded-full border-2 border-surface-dim shrink-0" />
                    )}
                    <span className={done ? 'text-green-700' : active ? 'text-on-surface font-medium' : 'text-on-surface-variant'}>
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-on-surface-variant text-center">
              Large imports may take a minute — please keep this window open.
            </p>
          </div>
        )}

        {step === 'preview' && preview && !importMutation.isLoading && !isBatchRunning && (
          <div className="p-6 space-y-5">
            {/* File info */}
            <p className="text-sm text-on-surface-variant">
              <span className="font-medium text-on-surface">{file?.name}</span>
              {' '}— {(file!.size / 1024).toFixed(1)} KB
            </p>

            {/* Summary counts */}
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-surface-container-low rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-on-surface">{preview.totalRows}</p>
                <p className="text-xs text-on-surface-variant mt-1">Total rows</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{preview.validRows}</p>
                <p className="text-xs text-green-700 mt-1">Will be attempted</p>
              </div>
              <div className={`rounded-lg p-4 text-center ${preview.missingEmailRows > 0 ? 'bg-yellow-50' : 'bg-surface-container-low'}`}>
                <p className={`text-2xl font-bold ${preview.missingEmailRows > 0 ? 'text-yellow-700' : 'text-on-surface-variant'}`}>
                  {preview.missingEmailRows}
                </p>
                <p className={`text-xs mt-1 ${preview.missingEmailRows > 0 ? 'text-yellow-700' : 'text-on-surface-variant'}`}>
                  Missing email (skipped)
                </p>
              </div>
            </div>

            {/* No email column warning */}
            {!preview.hasEmailColumn && (
              <div className="flex items-start gap-2 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
                <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 shrink-0" />
                <p>No <strong>email</strong> column detected. All rows will be skipped. Check your CSV headers match the template.</p>
              </div>
            )}

            {/* Duplicate note */}
            <p className="text-xs text-on-surface-variant">
              Customers already in your Stripe account with matching emails will be reported as skipped after import.
            </p>

            {/* Sample table */}
            {preview.sampleRows.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-on-surface-variant mb-2">
                  Preview — first {preview.sampleRows.length} row{preview.sampleRows.length !== 1 ? 's' : ''}
                  {preview.totalRows > 5 && ` of ${preview.totalRows}`}
                </h3>
                <div className="rounded-lg overflow-auto border border-surface-dim max-h-52">
                  <table className="min-w-full divide-y divide-surface-dim text-sm">
                    <thead className="bg-surface-container-low sticky top-0">
                      <tr>
                        {preview.headers.map(h => (
                          <th key={h} className="px-3 py-2 text-left font-medium text-on-surface-variant whitespace-nowrap">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-dim">
                      {preview.sampleRows.map((row, idx) => (
                        <tr key={idx} className={!row['email']?.trim() ? 'bg-yellow-50' : ''}>
                          {preview.headers.map(h => (
                            <td key={h} className="px-3 py-2 text-on-surface whitespace-nowrap max-w-[160px] truncate">
                              {row[h] || <span className="text-on-surface-variant italic">—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {preview.totalRows > 5 && (
                  <p className="text-xs text-on-surface-variant mt-1">
                    + {preview.totalRows - 5} more row{preview.totalRows - 5 !== 1 ? 's' : ''} not shown
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Step: Results ── */}
        {step === 'results' && results && (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircleIcon className="h-5 w-5" />
              <span className="font-semibold text-sm">Import complete</span>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-surface-container-low rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-on-surface">{results.totalRows}</p>
                <p className="text-xs text-on-surface-variant mt-1">Total rows</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-700">{results.created}</p>
                <p className="text-xs text-green-700 mt-1">Activated</p>
              </div>
              <div className={`rounded-lg p-4 text-center ${results.skipped > 0 ? 'bg-yellow-50' : 'bg-surface-container-low'}`}>
                <p className={`text-2xl font-bold ${results.skipped > 0 ? 'text-yellow-700' : 'text-on-surface-variant'}`}>
                  {results.skipped}
                </p>
                <p className={`text-xs mt-1 ${results.skipped > 0 ? 'text-yellow-700' : 'text-on-surface-variant'}`}>
                  Failed
                </p>
              </div>
            </div>

            {results.status === 'failed_compensated' && (
              <div className="flex items-start gap-2 bg-yellow-50 text-yellow-800 rounded-lg p-3 text-sm">
                <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 shrink-0" />
                <p>Batch failed after execution started, but all created records were compensated.</p>
              </div>
            )}

            {results.status === 'failed_manual_reconciliation_required' && (
              <div className="flex items-start gap-2 bg-red-50 text-red-800 rounded-lg p-3 text-sm">
                <ExclamationTriangleIcon className="h-4 w-4 mt-0.5 shrink-0" />
                <p>Batch failed and at least one compensation step failed. Manual reconciliation is required.</p>
              </div>
            )}

            {typeof results.compensatedRows === 'number' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-semibold text-blue-700">{results.compensatedRows}</p>
                  <p className="text-xs text-blue-700 mt-1">Compensated rows</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-semibold text-red-700">{results.compensationFailedRows || 0}</p>
                  <p className="text-xs text-red-700 mt-1">Compensation failed rows</p>
                </div>
              </div>
            )}

            {results.errors.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-on-surface-variant mb-2">
                  Row errors ({results.errors.length})
                </h3>
                <div className="rounded-lg overflow-auto border border-surface-dim max-h-52">
                  <table className="min-w-full divide-y divide-surface-dim text-sm">
                    <thead className="bg-surface-container-low sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-on-surface-variant">Row</th>
                        <th className="px-3 py-2 text-left font-medium text-on-surface-variant">Email</th>
                        <th className="px-3 py-2 text-left font-medium text-on-surface-variant">Reason</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-surface-dim">
                      {results.errors.map((err, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2 text-on-surface">{err.row}</td>
                          <td className="px-3 py-2 text-on-surface-variant truncate max-w-[150px]">{err.email}</td>
                          <td className="px-3 py-2 text-yellow-700">{err.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="h-px bg-surface-dim" />
        <div className="flex justify-between items-center p-6">
          <div>
            {step === 'preview' && !importMutation.isLoading && !isBatchRunning && (
              <button onClick={handleBack} className="text-sm text-on-surface-variant hover:text-on-surface font-medium">
                ← Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              disabled={importMutation.isLoading || isBatchRunning}
              className="px-4 py-2 text-on-surface-variant bg-surface-container-low hover:bg-surface-container rounded-lg font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {step === 'results' ? 'Done' : 'Cancel'}
            </button>

            {step === 'preview' && (
              <button
                onClick={handleConfirmImport}
                disabled={importMutation.isLoading || isBatchRunning || !preview?.hasEmailColumn || preview.validRows === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
              >
                {(importMutation.isLoading || isBatchRunning) ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span>{isBatchRunning ? 'Batch Running...' : 'Starting...'}</span>
                  </>
                ) : (
                  <>
                    <ArrowUpTrayIcon className="h-4 w-4" />
                    <span>Confirm &amp; Import {preview?.validRows} customer{preview?.validRows !== 1 ? 's' : ''}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
